import type { FilterableLooksQuery, LookFieldsFragment, LookGridCardFieldsFragment, LookQuery } from '#graphql-operations'
import type { CollectionIndex } from '~~/server/api/collection-index.get'
import { ROOT_COLLECTION } from '~/constants'

/**
 * Shop-the-look reads.
 *
 * The placement strings are the contract with `exalted-looks-plugin`'s
 * `look-placement.ts` in the backend repo, which deploys separately. Anything
 * this file does not recognise is not sent, so a placement added there is inert
 * here until the storefront ships too — the same one-way compatibility
 * `readProductRowPlacement` gives the homepage rails.
 */
export const LOOK_PLACEMENTS = ['looks-page', 'collection'] as const
export type LookPlacement = typeof LOOK_PLACEMENTS[number]

/**
 * One clause of the shopper's facet selection, exactly as the product search
 * takes it: OR within a clause, AND across them. `byProduct` absent means the
 * facet lives on the variant — colour and size in this shop.
 *
 * Structural rather than the generated input type, so a caller can hand over
 * `useFacetFilters`' memoised array without a cast.
 */
export interface LookFacetFilter {
  or: string[]
  byProduct?: boolean
}

/**
 * A look as its OWN page reads it: every garment's sizes, prices and stock, for
 * the purchase rows. `LookPageFields` spreads it, for `look` and `previewLook`.
 */
export type Look = LookFieldsFragment

/**
 * A look as a CARD renders it: the grid tile, the rails, the homepage row and the
 * wishlist. Photos, markers and each marker's product id, name, slug and
 * collections, but no prices or stock (`LookGridCardFields`). The card a marker
 * opens fetches those itself (`useLookHotspotProduct`).
 *
 * Anything that needs only a card's fields takes this, so the outfit page's full
 * `Look` still fits; anything that reads prices or stock takes `Look`, so a card
 * surface cannot hand it the lighter data without a type error.
 */
export type LookCardData = LookGridCardFieldsFragment
export type LookCardAsset = LookCardData['assets'][number]
export type LookCardHotspot = LookCardAsset['hotspots'][number]

/**
 * One entry of a filtering surface's index: an outfit's id and
 * `filterFacetValues`, the values the backend would match it on.
 *
 * No card fields. The cards behind these ids come in slices through
 * `useLookCards`, so the index stays light enough to read in one go.
 */
export type FilterableLook = FilterableLooksQuery['looks'][number]
export type LookAsset = Look['assets'][number]
export type LookHotspot = LookAsset['hotspots'][number]

/**
 * One look as its OWN page reads it: everything `LookFields` carries plus the
 * model, the per-garment sizes and the curated "more outfits" row, which the
 * `look` query selects outside the fragment.
 *
 * A type of its own rather than widening `Look`, because the model, the sizes
 * and the related row are selected outside the fragment, and nothing but this
 * page has them.
 */
export type LookDetail = NonNullable<LookQuery['look']>
export type LookModel = NonNullable<LookDetail['model']>

/** The first gallery photo — what the grid card and the homepage block render. */
export function heroAsset(look: LookCardData): LookCardAsset | null {
  return look.assets[0] ?? null
}

/**
 * Every marker across the gallery, de-duplicated by product: the same jacket is
 * tagged on several photos, and the purchase panel lists it once. Order is
 * gallery order then marker order, so the numbering matches the hero's markers
 * for the products that appear on it.
 */
export function uniqueHotspots(look: Look): LookHotspot[]
export function uniqueHotspots(look: LookCardData): LookCardHotspot[]
export function uniqueHotspots(look: LookCardData): LookCardHotspot[] {
  const seen = new Set<string>()
  return look.assets
    .flatMap(asset => asset.hotspots)
    .filter(hotspot => (seen.has(String(hotspot.product.id)) ? false : (seen.add(String(hotspot.product.id)), true)))
}

/**
 * Each product's 1-based number in `uniqueHotspots` order. The outfit page
 * numbers its purchase rows this way, and every marker across the gallery
 * shows the SAME number for the same garment — the jacket that is row 2 is
 * marker 2 on every photograph it appears on, not marker 1 on the second one.
 */
export function pieceNumbers(look: LookCardData): Record<string, number> {
  return Object.fromEntries(uniqueHotspots(look).map((hotspot, index) => [String(hotspot.product.id), index + 1]))
}

export interface LookCategory {
  id: string
  name: string
  slug: string
}

/**
 * The categories of an outfit's garments — one per garment, de-duplicated by
 * NAME, in marker order: "Tees & Tanks · Shorts" for a tank and a pair of
 * shorts. By name rather than id because the men's and the women's joggers are
 * two collections both called "Joggers", and a mixed outfit must not read
 * "Sweatshirts · Joggers · Joggers".
 *
 * The product page's breadcrumb rule, applied per garment: of a product's
 * collections the category is the most SPECIFIC one — it sits in a gender
 * root, an umbrella (`women-tops`) and the real category (`women-sportbra`)
 * at once, so the smallest by variant count wins, ties going to the
 * gender-scoped slug. The catalogue's collections are flat, so there is no
 * tree to read this from. Sizes come from the collection index; until it has
 * loaded (or for an id it does not know) nothing is picked, and the card
 * shows no categories rather than a guess.
 */
export function pieceCategories(look: LookCardData, index: CollectionIndex): LookCategory[] {
  const out = new Map<string, LookCategory>()
  for (const hotspot of uniqueHotspots(look)) {
    const category = categoryOf(hotspot.product.collections, index)
    const key = category?.name.trim().toLowerCase()
    if (category && key && !out.has(key))
      out.set(key, category)
  }
  return [...out.values()]
}

function categoryOf(collections: Array<{ id: string | number, name: string, slug: string }>, index: CollectionIndex): LookCategory | undefined {
  const slugs = new Set(collections.map(collection => index[String(collection.id)]?.slug ?? collection.slug))
  const women = slugs.has('women')
  const men = slugs.has('men')
  const ownRoot = women !== men ? (women ? 'women' : 'men') : undefined
  const otherRoot = ownRoot === 'women' ? 'men' : ownRoot === 'men' ? 'women' : undefined

  const candidates = collections
    .flatMap((collection) => {
      const entry = index[String(collection.id)]
      if (!entry || !collection.name)
        return []
      // The gender roots name the shopper, not the garment; the root holds
      // everything; and a product carrying both genders' accessory
      // collections must not cross over.
      if (entry.slug === 'women' || entry.slug === 'men' || entry.slug === ROOT_COLLECTION)
        return []
      if (otherRoot && entry.slug.startsWith(`${otherRoot}-`))
        return []
      return [{
        id: String(collection.id),
        name: collection.name,
        slug: entry.slug,
        size: entry.size,
        genderScoped: Boolean(ownRoot && entry.slug.startsWith(`${ownRoot}-`)),
      }]
    })
    .sort((a, b) =>
      a.size - b.size
      || Number(b.genderScoped) - Number(a.genderScoped)
      || a.slug.localeCompare(b.slug))

  const best = candidates[0]
  return best ? { id: best.id, name: best.name, slug: best.slug } : undefined
}

/** Narrow a value to a placement the backend will answer for. */
export function readLookPlacement(raw: string | null | undefined): LookPlacement | null {
  return LOOK_PLACEMENTS.includes(raw as LookPlacement) ? (raw as LookPlacement) : null
}

/**
 * Looks for one surface.
 *
 * Server-rendered on a collection page, client-only everywhere else.
 *
 * On a collection page the looks are cards IN the product grid, from its fifth
 * cell, so as a client-side read they held a pulsing placeholder there until
 * hydration — which `delayHydration` defers to the first interaction or 5-7s of
 * idle — and only then sent the request. Server-rendered, they arrive in the
 * first byte with the products around them. What argued against that is gone:
 * the read is cheap now that it selects no prices or stock
 * (`LookGridCardFields`), the render is edge-cached with the page's HTML, and
 * the response no longer spends the HTML's 47-tag cache-tag budget, because its
 * tags collapse to `op:looks` (`OPERATION_ONLY_HTML_OPERATIONS` in
 * htmlCacheTags.ts) where the garments' own `p:`/`c:` tags would have spilled
 * the whole page. The facet selection resolves on the server too
 * (`primeFacetSelection`), so a filtered URL renders the filtered outfits rather
 * than hydrating into them.
 *
 * `looks-page` stays client-only: through `useLooks` that is `LookMoreNearby`'s
 * pool, the fallback "More outfits" row below the fold on an outfit page.
 *
 * No `default` option, matching `productUpsells` on the PDP. Callers coalesce
 * with `?? []` instead. Nuxt seeds the SSR payload for a `server: false` entry,
 * and a `default` there can be mistaken for cached data on hydration, so the
 * client-only branch follows the precedent known to work in production.
 */
export function useLooks(
  placement: LookPlacement,
  collectionSlug?: MaybeRefOrGetter<string | null | undefined>,
  facetValueFilters?: MaybeRefOrGetter<LookFacetFilter[] | null | undefined>,
) {
  const slug = computed(() => toValue(collectionSlug) ?? null)
  /**
   * Null, never `[]`, when nothing is selected: the key and the variables both
   * read it, and an empty array would give an unfiltered listing a different
   * cache key on every page from the one SSR warmed.
   */
  const filters = computed(() => {
    const value = toValue(facetValueFilters)
    return value?.length ? value : null
  })

  return useAsyncData(
    () => `looks:${placement}:${slug.value ?? '-'}:${filters.value ? JSON.stringify(filters.value) : '-'}`,
    async () => {
      // The backend answers `collection` only with a slug, so skip the round
      // trip entirely rather than sending a request that cannot match.
      if (placement === 'collection' && !slug.value)
        return [] as LookCardData[]
      const result = await useGraphqlQuery('looks', {
        placement,
        collectionSlug: slug.value,
        facetValueFilters: filters.value,
      })
      return (result.data?.looks ?? []) as LookCardData[]
    },
    // `filters` is watched by identity, and `useFacetFilters` memoises the
    // array it returns for exactly this reason — a fresh reference per
    // evaluation would re-run this read on every unrelated render.
    { server: placement === 'collection', watch: [slug, filters] },
  )
}

/**
 * The index of a surface that filters its looks: each placed outfit's id and
 * the values it would filter as, in render order. `/looks` today, which reads
 * the cards themselves in slices through `useLookCards`.
 *
 * No `facetValueFilters` argument, deliberately, unlike `useLooks`: this reads
 * the surface's WHOLE set once and the page narrows it in the browser. The
 * chips have to be drawn from the unfiltered set anyway — the product drawer
 * counts the page's whole result set, not the shopper's current narrowing — so
 * asking the server again per chip would fetch a second, smaller list the page
 * already holds, and pay a round-trip for every tap.
 *
 * It is the same verdict either way: `filterFacetValues` is the rule the
 * `facetValueFilters` argument executes, projected ahead of time.
 */
export function useFilterableLooks(
  placement: LookPlacement,
  collectionSlug?: MaybeRefOrGetter<string | null | undefined>,
) {
  const slug = computed(() => toValue(collectionSlug) ?? null)

  return useAsyncData(
    () => `filterable-looks:${placement}:${slug.value ?? '-'}`,
    async () => {
      // The backend answers `collection` only with a slug; see `useLooks`.
      if (placement === 'collection' && !slug.value)
        return [] as FilterableLook[]
      const result = await useGraphqlQuery('filterableLooks', {
        placement,
        collectionSlug: slug.value,
      })
      return (result.data?.looks ?? []) as FilterableLook[]
    },
    /**
     * Server-rendered, unlike `useLooks`, and the reason is the surface rather
     * than the data: this is the whole of `/looks`, not a rail beside something
     * else. Client-side it was the page's ONLY content, so the HTML carried a
     * heading and nothing, and the grid could not start arriving until
     * hydration had — which `delayHydration` holds until the first interaction
     * or idle plus 5-7s. Measured: 0 outfit cards in the markup, then a
     * 1.1-2.8s request, against a collection page that ships 69 product cards
     * in its first byte.
     *
     * The cost that argued for `server: false` — a blocking read that usually
     * returns nothing — does not apply either: this surface is the one whose
     * entire purpose is the outfits, so the read is never wasted. It is also
     * light now: ids and filter values, no photos or garments.
     *
     * `/looks` is in `cacheableHtmlRouteRules` as of the same change, so the
     * render is amortised at the edge rather than paid per visit.
     */
    { watch: [slug] },
  )
}

/**
 * Looks that contain one product — the PDP's "complete the look".
 *
 * Same no-`default` rule as `useLooks`; see the note there.
 */
export function useProductLooks(productId: MaybeRefOrGetter<string | number | undefined>) {
  const id = computed(() => {
    const value = toValue(productId)
    return value === undefined ? null : String(value)
  })

  return useAsyncData(
    () => `product-looks:${id.value ?? '-'}`,
    async () => {
      if (!id.value)
        return [] as LookCardData[]
      const result = await useGraphqlQuery('productLooks', { id: id.value })
      return (result.data?.product?.looks ?? []) as LookCardData[]
    },
    { server: false, watch: [id] },
  )
}

/**
 * One look for its own page, `/looks/:id`.
 *
 * Server-rendered, unlike the placed reads: here the look IS the page, the
 * route is not in `cacheableHtmlRouteRules`, and a missing look has to become a
 * 404 before any HTML is sent — none of which a client-only fetch can do.
 */
export function useLook(
  id: MaybeRefOrGetter<string | undefined>,
  preview: MaybeRefOrGetter<boolean> = false,
) {
  const key = computed(() => toValue(id) ?? null)
  const isPreview = computed(() => toValue(preview) === true)

  return useAsyncData(
    // A key of its own for the preview, never the live one: the two return
    // different things for the same id — one honours `enabled`, the other does
    // not — and sharing a key would let a preview of a switched-off outfit be
    // served to a shopper from the payload, or the other way round.
    () => (isPreview.value ? `preview-look:${key.value ?? '-'}` : `look:${key.value ?? '-'}`),
    async () => {
      if (!key.value)
        return null
      if (isPreview.value) {
        const result = await useGraphqlQuery('previewLook', { id: key.value })
        return (result.data?.previewLook ?? null) as LookDetail | null
      }
      const result = await useGraphqlQuery('look', { id: key.value })
      return (result.data?.look ?? null) as LookDetail | null
    },
    { watch: [key, isPreview] },
  )
}

/**
 * The outfit page's hand-picked "you may also like" row.
 *
 * `server: false`, unlike the look itself: this row sits below the fold and the
 * page's first byte should not wait on a second round trip. `/looks/**` is NOT
 * in `cacheableHtmlRouteRules`, so unlike the placed reads there is no
 * cache-tag budget arguing for it — only the waterfall, which is the right
 * trade for a row nobody sees until they scroll.
 *
 * No `default`, matching `useLooks` above; callers coalesce with `?? []`.
 */
export function useLookUpsells(id: MaybeRefOrGetter<string | undefined>) {
  const key = computed(() => toValue(id) ?? null)
  return useAsyncData(
    () => `look-upsells:${key.value ?? '-'}`,
    async () => {
      if (!key.value)
        return []
      const result = await useGraphqlQuery('lookUpsells', { id: key.value })
      return result.data?.look?.upsellProducts ?? []
    },
    { server: false, watch: [key] },
  )
}

/**
 * The outfits hand-picked to sit alongside this one.
 *
 * `server: false` for the same reason as the upsell row: it spreads a whole
 * look card over every partner, and keeping it off the outfit page's
 * SSR-blocking `look` query is the whole point of it being a separate
 * operation. An EMPTY array is meaningful — it means "nothing was curated", and
 * `LookMoreRow` turns that into its shared-category fallback.
 */
export function useLookRelated(id: MaybeRefOrGetter<string | undefined>) {
  const key = computed(() => toValue(id) ?? null)
  return useAsyncData(
    () => `look-related:${key.value ?? '-'}`,
    async () => {
      if (!key.value)
        return [] as LookCardData[]
      const result = await useGraphqlQuery('lookRelated', { id: key.value })
      return (result.data?.look?.relatedLooks ?? []) as LookCardData[]
    },
    { server: false, watch: [key] },
  )
}

/**
 * The outfits one model is wearing — the row between the profile and the
 * products.
 *
 * The one look read in this file that is NOT `server: false`, and the reasons
 * `useLooks` gives for that flag are the reasons it does not apply here. Those
 * surfaces serve cacheable HTML and a blocking read would cost every visitor
 * latency to render nothing; `/models/**` has no entry in
 * `cacheableHtmlRouteRules`, so there is no cached HTML to protect and no
 * 47-entity tag budget for the row's products to eat into. What is left is the
 * cost of the request itself, and that is paid concurrently — see below.
 */
export function useProductModelLooks(key: MaybeRefOrGetter<string | undefined>) {
  const modelKey = computed(() => toValue(key) ?? null)
  return useAsyncData(
    () => `model-looks:${modelKey.value ?? '-'}`,
    async () => {
      if (!modelKey.value)
        return [] as LookCardData[]
      const result = await useGraphqlQuery('productModelLooks', { key: modelKey.value })
      return (result.data?.productModelLooks ?? []) as LookCardData[]
    },
    /**
     * Server-rendered, unlike every other look read in this file.
     *
     * The rail sits BETWEEN the profile and the product grid, so arriving at
     * hydration meant inserting ~580px above the grid and shoving it down —
     * `LookRail` renders nothing until the data lands, so there is no space to
     * reserve and nothing to reserve it for on the nine models in ten that have
     * no outfit. The measured shift only ever hit that tenth model, but on those
     * it was most of a viewport.
     *
     * It is close to free here because it is NOT awaited: the promise starts at
     * setup and Nuxt collects it alongside the model, the product search and the
     * card details, so it overlaps legs the page already pays for rather than
     * adding one. `productModelLooks` is edge-cached at 3600
     * (`server/graphqlMiddleware.serverOptions.ts`), so the repeat cost is a
     * cache hit.
     */
    { watch: [modelKey] },
  )
}

/**
 * Is every size of this product sold out?
 *
 * `stockLevel` is a STRING on the Shop API, not a number — Vendure's default
 * strategy returns 'IN_STOCK' / 'OUT_OF_STOCK' rather than a count, so a
 * numeric comparison here would be true for every product and mark the whole
 * catalogue sold out.
 */
export function isProductSoldOut(product?: { variants?: Array<{ stockLevel: string }> | null } | null): boolean {
  const variants = product?.variants ?? []
  if (!variants.length)
    return true
  return variants.every(variant => variant.stockLevel === 'OUT_OF_STOCK')
}

/** The variant a "shop the set" row defaults to: the first one still in stock. */
export function firstAvailableVariantOf<T extends { stockLevel: string }>(product?: { variants?: T[] | null } | null): T | null {
  const variants = product?.variants ?? []
  return variants.find(variant => variant.stockLevel !== 'OUT_OF_STOCK') ?? null
}
